// Withdraw a SCHEDULED activation (spec 01 §3.3.5, §9.16, D-261) — the one
// dialog both the BOM editor (S3) and the routing editor (S4) open.
//
// What it asks for: the required `reason` (the change-log row this writes is
// the light ECO record, like the activation note it undoes). What it says
// before the human commits: which version is going to the archive, the date
// it would have taken effect, and which version comes BACK — or that none
// will and the product will have no active version (legal: §3.4 answers
// NO_EFFECTIVE_BOM), or that the predecessor has been archived and the server
// will REFUSE (PREDECESSOR_NOT_RESTORABLE), or that this screen cannot tell.
// Each is a distinct fact and gets its own sentence (`RestoreSummary`); the
// first cut folded the archived case into "nothing will be restored", which
// is a different fact and the wrong one. The caller captures the preview when
// the dialog OPENS and hands that in (the editors' `withdrawDialog` state):
// a refetch landing mid-dialog — a background revalidation, or the one a
// refusal triggers — used to recompute it live and could flip "Version 1 will
// return" to "nothing will be restored" under the planner's decision. Once a
// refusal is on screen the preview is hidden altogether: it is moot for
// VERSION_IN_FORCE / VERSION_NOT_ACTIVE and contradicts
// PREDECESSOR_NOT_RESTORABLE outright, and a refused dialog shows one fact.
//
// The three §3.3.5 refusals each get their own sentence (VERSION_IN_FORCE,
// VERSION_NOT_ACTIVE, PREDECESSOR_NOT_RESTORABLE — `withdrawRefusalOf`); a
// refusal is a stable fact about the row, and a generic "could not withdraw"
// would send the planner back to retry a thing that will refuse again.
//
// Built on the `Modal` primitive: focus moves in, Tab cycles, Escape and the
// scrim close — EXCEPT while the withdraw is in flight. The archive happens
// only on the explicit Withdraw press, so closing is the safe outcome up to
// that press; after it, an exit would unmount the dialog under a live request
// (the caller's `onCancel` resets the mutation, and the result would land on
// nothing). Both halves of the guard are here: the dialog's own `onCancel`
// is a no-op while pending, and the Modal's `pending` prop makes Escape, the
// scrim and the ✕ inert at the primitive.

import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { Banner } from "../../../../components/toast";
import { problemTitle } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import {
  refusalEffectiveFromOf,
  refusalTodayOf,
  withdrawRefusalOf,
  type RestoreSummary,
} from "./version-schedule";

export interface WithdrawDialogProps {
  /** The version being withdrawn and the date it was scheduled for. */
  version: number;
  effectiveFrom: string;
  /** What the withdrawal is expected to bring back (`restorePreview`) — as
   * captured when the dialog opened, never recomputed under it. */
  restore: RestoreSummary;
  pending: boolean;
  /** The withdraw mutation's error, if any — mapped to a specific sentence. */
  error: unknown;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  testId?: string | undefined;
}

export function WithdrawDialog({
  version,
  effectiveFrom,
  restore,
  pending,
  error,
  onConfirm,
  onCancel,
  testId,
}: WithdrawDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const reasonMissing = reason.trim().length === 0;
  const hintId = useId();
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  // Initial focus lands on the reason field — the one thing the dialog asks
  // for — not on the Modal's ✕ (its "first focusable"). This effect belongs to
  // the PARENT of Modal, and React runs a child's effects before its
  // parent's, so it runs after Modal has placed focus and wins.
  useEffect(() => {
    reasonRef.current?.focus();
  }, []);

  const refusal = error === null || error === undefined ? null : withdrawRefusalOf(error);
  const requestId = requestIdOf(error);
  const serverToday = refusalTodayOf(error);
  const errorText =
    error === null || error === undefined
      ? null
      : refusal === "inForce"
        ? t("catalogue.withdraw.refusal.inForce", {
            // The server's own date for the row where it named one.
            date: refusalEffectiveFromOf(error) ?? effectiveFrom,
          })
        : refusal === "notActive"
          ? t("catalogue.withdraw.refusal.notActive")
          : refusal === "predecessorNotRestorable"
            ? t("catalogue.withdraw.refusal.predecessorNotRestorable")
            : (problemTitle(error) ?? t("catalogue.withdraw.failed"));
  // A refusal will refuse again: the only honest control left is Close.
  const refused = refusal !== null;

  // No exit while the request is in flight — see the header.
  const cancel = () => {
    if (pending) return;
    onCancel();
  };

  return (
    <Modal
      heading={t("catalogue.withdraw.title")}
      label={t("catalogue.withdraw.title")}
      closeLabel={t("catalogue.withdraw.close")}
      onClose={cancel}
      pending={pending}
      className="bom-withdraw-modal"
      testId={testId ?? "withdraw-dialog"}
    >
      <div className="bom-withdraw-body">
        <p className="bom-withdraw-lead">
          {t("catalogue.withdraw.lead", { version, date: effectiveFrom })}
        </p>

        {/* The preview is moot once refused — the refusal is the one fact. */}
        {refused ? null : restore.kind === "restores" ? (
          <Banner kind="neutral" data-testid="withdraw-restore-preview" data-restore="restores">
            {t("catalogue.withdraw.restorePreview", { version: restore.version })}
          </Banner>
        ) : restore.kind === "notRestorable" ? (
          <Banner kind="warn" data-testid="withdraw-restore-preview" data-restore="notRestorable">
            {t("catalogue.withdraw.notRestorablePreview", { version: restore.version })}
          </Banner>
        ) : restore.kind === "none" ? (
          <Banner kind="warn" data-testid="withdraw-restore-preview" data-restore="none">
            {t("catalogue.withdraw.noRestorePreview")}
          </Banner>
        ) : (
          <Banner kind="neutral" data-testid="withdraw-restore-preview" data-restore="unknown">
            {t("catalogue.withdraw.unknownRestorePreview")}
          </Banner>
        )}

        <Field label={t("catalogue.withdraw.reason")} wide>
          <textarea
            ref={reasonRef}
            rows={3}
            value={reason}
            required
            aria-required="true"
            aria-describedby={refused ? undefined : hintId}
            placeholder={t("catalogue.withdraw.reasonPlaceholder")}
            disabled={pending || refused}
            data-testid="withdraw-reason"
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
        </Field>
        {refused ? null : (
          <p id={hintId} className="bom-hint" data-testid="withdraw-reason-hint">
            {t("catalogue.withdraw.reasonRequired")}
          </p>
        )}

        {errorText !== null ? (
          <Banner kind="bad" data-testid="withdraw-error" data-refusal={refusal ?? "other"}>
            {errorText}
            {serverToday !== undefined && refusal === "inForce" ? (
              <> {t("catalogue.withdraw.refusal.serverToday", { today: serverToday })}</>
            ) : null}
            {requestId !== undefined ? (
              <span className="rte-request-id">
                {" "}
                {t("catalogue.state.requestId")}: <code>{requestId}</code>
              </span>
            ) : null}
          </Banner>
        ) : null}
      </div>

      <div className="bom-withdraw-foot">
        <Button onClick={cancel} disabled={pending} data-testid="withdraw-dismiss">
          {refused ? t("catalogue.withdraw.close") : t("catalogue.withdraw.cancel")}
        </Button>
        {refused ? null : (
          <Button
            variant="danger"
            disabled={reasonMissing || pending}
            busy={pending}
            busyLabel={t("catalogue.withdraw.withdrawing")}
            data-testid="withdraw-confirm"
            onClick={() => {
              onConfirm(reason.trim());
            }}
          >
            {t("catalogue.withdraw.confirm")}
          </Button>
        )}
      </div>
    </Modal>
  );
}
